/**
 * Token → JS color bridge.
 *
 * `getComputedStyle(el).getPropertyValue('--color-foreground')` does **not**
 * return a color. Per the custom-properties spec the computed value of an
 * unregistered custom property is its token stream with `var()` substituted —
 * nothing more. For a literal token that happens to look like a color
 * (`#0a0a0a`), but for a derived one it comes back as `hsl(200 calc(…) 6%)` or
 * `color-mix(in srgb, …)`, which no JS color parser handles.
 *
 * To get a real color you have to make the browser *use* the token in a property
 * that computes to a color. That is what the probe below does: one off-screen
 * span per token with `color: var(--token)`, read back as `rgb(…)`.
 *
 * This is also why every derived token in `tokens.css` stays inside sRGB —
 * `oklab()` / `oklch()` and `color-mix()` resolve to strings zrender's color
 * parser rejects (see `useChartPalette`), so they must never reach a chart even
 * through this probe.
 */

export interface ProbeOptions {
  /** `data-theme` for the probe. Omit to read the palette currently painted. */
  theme?: string
  /** `data-scheme` for the probe. Omit to inherit the document's. */
  scheme?: 'light' | 'dark'
  /** Custom properties to set on the probe, e.g. `{ '--seed-hue': '200' }`. */
  vars?: Record<string, string>
}

const OFFSCREEN =
  'position:fixed;left:-9999px;top:0;width:0;height:0;overflow:hidden;pointer-events:none'

/**
 * An invalid `var()` makes `color` invalid at computed-value time, and `color`
 * then falls back to the *inherited* value — a perfectly plausible-looking color.
 * So the host paints a sentinel no real token uses, and a reference cell reports
 * whatever string the browser canonicalises it to; cells matching it are
 * unresolved rather than magenta. The alpha matters: every token in this project
 * is opaque, so an opaque token can never collide with it.
 */
const SENTINEL = 'rgba(255, 0, 255, 0.5)'

/**
 * Resolves each token to the `rgb(…)` / `rgba(…)` string the browser computes.
 *
 * Custom properties inherit, so with no `theme` the probe sees exactly the
 * palette on `:root`. Passing `theme` lets a candidate palette be measured
 * without painting it — this requires the theme's block in `tokens.css` to also
 * match `.theme-probe[data-theme='…']`, which the custom-theme blocks do.
 *
 * Tokens that do not resolve to a color — undeclared, or holding a non-color like
 * `--seed-hue` — are omitted from the result rather than reported as a wrong one.
 */
export function resolveTokenColors(
  tokens: readonly string[],
  opts: ProbeOptions = {},
): Record<string, string> {
  const host = document.createElement('div')
  host.className = 'theme-probe'
  host.style.cssText = OFFSCREEN
  host.style.color = SENTINEL
  if (opts.theme) host.setAttribute('data-theme', opts.theme)
  if (opts.scheme) host.setAttribute('data-scheme', opts.scheme)
  for (const [k, v] of Object.entries(opts.vars ?? {})) host.style.setProperty(k, v)

  const reference = document.createElement('span')
  host.appendChild(reference)

  const cells = tokens.map((token) => {
    const cell = document.createElement('span')
    // `color` is the one property guaranteed to compute to a color and to be
    // readable without layout.
    cell.style.color = `var(${token})`
    host.appendChild(cell)
    return cell
  })

  // Must be in the document for getComputedStyle to resolve anything.
  document.body.appendChild(host)
  try {
    const unresolved = getComputedStyle(reference).color
    const out: Record<string, string> = {}
    tokens.forEach((token, i) => {
      const v = getComputedStyle(cells[i]).color
      if (v && v !== unresolved) out[token] = v
    })
    return out
  } finally {
    host.remove()
  }
}

/** Parses the `rgb(r, g, b)` / `rgba(r, g, b, a)` form the probe returns. */
export function parseRgb(value: string): [number, number, number] | null {
  const m = value.match(/-?[\d.]+/g)
  if (!m || m.length < 3) return null
  const [r, g, b] = m.slice(0, 3).map(Number)
  if ([r, g, b].some((n) => !Number.isFinite(n))) return null
  return [r, g, b]
}
